import { cn } from '@/lib/utils';
import type { TicketPriority, TicketCategory } from '@ventin/shared';

/**
 * Shared ticket taxonomy UI (plan §4.4 / D10) — priority and category labels,
 * option lists for `<select>`s, and small colored badges. Reused across the
 * admin and portal ticket surfaces so labels/colors stay consistent.
 */

/** Client-friendly priority labels (default NORMAL). */
export const PRIORITY_LABELS: Record<TicketPriority, string> = {
  LOW: 'Low',
  NORMAL: 'Normal',
  HIGH: 'High',
  URGENT: 'Urgent',
};

/** Client-friendly category labels (default CONTENT_UPDATE). */
export const CATEGORY_LABELS: Record<TicketCategory, string> = {
  CONTENT_UPDATE: 'Content update',
  BUG: 'Bug',
  EMERGENCY: 'Emergency',
  QUESTION: 'Question',
  MAINTENANCE: 'Maintenance',
};

/** `<option>` source lists, ordered least→most urgent / logical grouping. */
export const PRIORITY_OPTIONS: ReadonlyArray<{ value: TicketPriority; label: string }> = (
  ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const
).map((value) => ({ value, label: PRIORITY_LABELS[value] }));

export const CATEGORY_OPTIONS: ReadonlyArray<{ value: TicketCategory; label: string }> = (
  ['CONTENT_UPDATE', 'BUG', 'EMERGENCY', 'QUESTION', 'MAINTENANCE'] as const
).map((value) => ({ value, label: CATEGORY_LABELS[value] }));

const PRIORITY_STYLES: Record<TicketPriority, string> = {
  LOW: 'bg-[#F1F1F3] text-[#4B4B52]',
  NORMAL: 'bg-blue-50 text-blue-700',
  HIGH: 'bg-amber-50 text-amber-700',
  URGENT: 'bg-red-50 text-red-700',
};

const CATEGORY_STYLES: Record<TicketCategory, string> = {
  CONTENT_UPDATE: 'bg-slate-100 text-slate-700',
  BUG: 'bg-red-50 text-red-700',
  EMERGENCY: 'bg-red-100 text-red-800',
  QUESTION: 'bg-indigo-50 text-indigo-700',
  MAINTENANCE: 'bg-[#F1F1F3] text-[#4B4B52]',
};

const chipClass = 'inline-flex items-center rounded-full px-[10px] py-1 text-[12px] font-medium';

export function PriorityBadge({ priority }: { priority: TicketPriority }): JSX.Element {
  return (
    <span className={cn(chipClass, PRIORITY_STYLES[priority])}>{PRIORITY_LABELS[priority]}</span>
  );
}

export function CategoryBadge({ category }: { category: TicketCategory }): JSX.Element {
  return (
    <span className={cn(chipClass, CATEGORY_STYLES[category])}>{CATEGORY_LABELS[category]}</span>
  );
}
